import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'

const editPrizeSchema = z.object({
  quantity: z.number().min(1, 'Số lượng phải lớn hơn 0'),
})

type EditPrizeFormData = z.infer<typeof editPrizeSchema>

interface EditPrizeFormProps {
  prize: any
  onSubmit: (data: EditPrizeFormData) => void
  loading?: boolean
  onCancel: () => void
}

export function EditPrizeForm({
  prize,
  onSubmit,
  loading,
  onCancel,
}: EditPrizeFormProps) {
  const form = useForm<EditPrizeFormData>({
    resolver: zodResolver(editPrizeSchema),
    defaultValues: {
      quantity: prize.quantity || 1,
    },
  })

  const getPrizeTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      PRODUCT: 'Sản phẩm',
      POINTS: 'Điểm',
      VOUCHER: 'Voucher',
      NOTHING: 'Không có giải',
    }
    return labels[type.toUpperCase()] || type
  }

  const getPrizeTypeBadge = (type: string) => {
    const variants: Record<string, any> = {
      PRODUCT: 'default',
      POINTS: 'secondary',
      VOUCHER: 'default',
      NOTHING: 'outline',
    }
    return (
      <Badge variant={variants[type.toUpperCase()] || 'outline'}>
        {getPrizeTypeLabel(type)}
      </Badge>
    )
  }

  const getPrizeDisplayInfo = () => {
    switch (prize.prizeType?.toUpperCase()) {
      case 'PRODUCT':
        return prize.productId || 'Sản phẩm'
      case 'POINTS':
        return `${prize.pointsAmount?.toLocaleString() || 0} điểm`
      case 'VOUCHER':
        return prize.voucherCode || prize.voucherId || 'Voucher'
      case 'NOTHING':
        return 'Chúc bạn may mắn lần sau'
      default:
        return 'Không xác định'
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Chỉnh Sửa Giải Thưởng</CardTitle>
        <CardDescription>Cập nhật thông tin giải thưởng</CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            {/* Display Prize Info (Read-only) */}
            <div className='space-y-3 rounded-lg bg-muted p-4'>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium text-muted-foreground'>
                    Loại giải thưởng
                  </label>
                  <div className='mt-1'>
                    {getPrizeTypeBadge(prize.prizeType)}
                  </div>
                </div>
              </div>
              <div>
                <label className='text-sm font-medium text-muted-foreground'>
                  Thông tin
                </label>
                <p className='mt-1 text-sm'>{getPrizeDisplayInfo()}</p>
              </div>
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <label className='text-sm font-medium text-muted-foreground'>
                    Đã phát
                  </label>
                  <p className='mt-1 text-sm font-semibold'>
                    {(
                      (prize.quantity || 0) - (prize.remainingQuantity || 0)
                    ).toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className='text-sm font-medium text-muted-foreground'>
                    Xác suất
                  </label>
                  <p className='mt-1 text-sm font-semibold'>
                    {prize.probability
                      ? `${prize.probability}%`
                      : 'Chưa cấu hình'}
                  </p>
                </div>
              </div>
            </div>

            {/* Editable Fields */}
            <FormField
              control={form.control}
              name='quantity'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Số lượng *</FormLabel>
                  <FormControl>
                    <Input
                      type='number'
                      min={prize.quantity - prize.remainingQuantity} // Can't reduce below distributed amount
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value))}
                    />
                  </FormControl>
                  <FormDescription>
                    Số lượng tối thiểu:{' '}
                    {(
                      prize.quantity - prize.remainingQuantity
                    ).toLocaleString()}{' '}
                    (đã phát)
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className='flex gap-2 pt-4'>
              <Button
                type='button'
                variant='outline'
                onClick={onCancel}
                disabled={loading}
                className='flex-1'
              >
                Hủy
              </Button>
              <Button type='submit' disabled={loading} className='flex-1'>
                {loading && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
                Cập Nhật
              </Button>
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  )
}
